/*
 * Purpose: Connect hub — setup, secrets, connectors — P4
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import type { ReactNode } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { Key, Library, Plug, Wand2, ChevronRight, Cable } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { PageMotion } from '@/components/motion/PageMotion';
import { SetupWizard } from '@/components/setup/SetupWizard';
import { SetupBanner } from '@/components/setup/SetupBanner';
import { SecretManager } from '@/components/SecretManager';
import { ConnectorLibraryBrowser } from '@/components/ConnectorLibraryBrowser';
import { McpConnectorPanel } from '@/components/McpConnectorPanel';
import { WorkflowIntegration } from '@/components/WorkflowIntegration';
import { isCoreSetupComplete } from '@/services/setup/setupStatus';

const LINKS = [
  {
    to: '/connect/setup',
    icon: Wand2,
    title: 'Onboarding',
    description: 'First-run vault, Google Cloud, Gmail, optional LinkedIn',
  },
  {
    to: '/connect/secrets',
    icon: Key,
    title: 'Secrets vault',
    description: 'API keys and credentials for connectors',
  },
  {
    to: '/connect/library',
    icon: Library,
    title: 'Connector library',
    description: 'Reusable blocks and integration catalog',
  },
  {
    to: '/connect/mcp',
    icon: Cable,
    title: 'MCP servers',
    description: 'Model Context Protocol — connect remote tool servers',
  },
  {
    to: '/connect/integrations',
    icon: Plug,
    title: 'External apps',
    description: 'Linked services and OAuth connections',
  },
];

function ConnectHub() {
  const readiness = isCoreSetupComplete();

  return (
    <PageMotion variant="body">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Connect</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Unlock compose and live runs — vault, connectors, and onboarding in one place.
          </p>
        </div>
        <SetupBanner />
        {!readiness ? (
          <Card className="p-4 border-amber-500/30 bg-amber-500/5">
            <p className="text-sm font-medium text-foreground">Setup needed before live Gmail flows</p>
            <p className="text-xs text-muted-foreground mt-1">
              Complete onboarding or add secrets to unlock full compose readiness.
            </p>
            <Link
              to="/connect/setup"
              className="inline-flex items-center text-xs font-medium text-primary mt-3 hover:underline"
            >
              Open onboarding
              <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
            </Link>
          </Card>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          {LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className="block group">
                <Card className="p-4 h-full transition-colors hover:border-primary/30 ui-lift-card-subtle">
                  <Icon className="h-5 w-5 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))] mb-2" />
                  <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                    {item.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </PageMotion>
  );
}

function ConnectSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-4">
      <h1 className="text-xl font-bold text-foreground">{title}</h1>
      {children}
    </div>
  );
}

export default function ConnectPage() {
  return (
    <Routes>
      <Route index element={<ConnectHub />} />
      <Route
        path="setup"
        element={
          <ConnectSection title="Onboarding Wizard">
            <SetupWizard />
          </ConnectSection>
        }
      />
      <Route
        path="secrets"
        element={
          <ConnectSection title="Secrets vault">
            <SecretManager />
          </ConnectSection>
        }
      />
      <Route
        path="library"
        element={
          <ConnectSection title="Connector library">
            <ConnectorLibraryBrowser />
          </ConnectSection>
        }
      />
      <Route
        path="mcp"
        element={
          <ConnectSection title="MCP servers">
            <McpConnectorPanel />
          </ConnectSection>
        }
      />
      <Route
        path="integrations"
        element={
          <ConnectSection title="External apps">
            <WorkflowIntegration />
          </ConnectSection>
        }
      />
      <Route path="*" element={<Navigate to="/connect" replace />} />
    </Routes>
  );
}
